import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * The top of every console page. Appearance is `.page-header` in `styles/ui.css`.
 *
 * One component rather than a hand-rolled `<header>` per route, because the two
 * things that go wrong are both invisible in isolation: page titles drifting a
 * size apart from each other, and the primary action landing in a different
 * corner on each screen. Both only show up when someone navigates between two
 * pages, which is exactly when nobody is looking at the code.
 *
 * There is deliberately **no eyebrow**. It used to carry the rail group a page
 * belonged to - "Monitor" above "Overview" - which put the same word on screen
 * twice, once in the rail and once six pixels from the title it was labelling.
 * The rail is where you are; the title is what you are looking at.
 *
 * **The description sits beside the title, not under it.** A console page is a
 * working surface and the header is not the work - a title, a sentence and a
 * button stacked over three rows was pushing the first table row a third of the
 * way down the screen. Baseline-aligned and a step smaller, it reads as an
 * annotation on the title rather than as a paragraph, which is all it ever was.
 *
 * That makes the length of a `description` a real constraint: it has to fit
 * beside a title on a laptop. One clause. If a page needs a paragraph to explain
 * itself, the paragraph belongs next to the thing it is explaining.
 *
 * `badge` is for a fact about the *page* rather than about the data on it -
 * today that is "Coming soon", on the two screens that are a preview of work not
 * built yet. It sits between the title and the description, which is the only
 * place a reader is guaranteed to see it before they start trying to use the
 * screen. It is not a home for a count or a status: those describe the rows, and
 * the rows can say so themselves.
 */
export function PageHeader({
  title,
  badge,
  description,
  actions,
  className,
}: {
  title: string;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("page-header", className)}>
      <div className="page-header__text">
        <h1 className="page-header__title">{title}</h1>
        {badge}
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}
